import { zodResolver } from '@hookform/resolvers/zod';
import { type CreateInquiryInput, PhoneSchema } from '@resolute/schema';
import { Button, cn, Input, Select } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarDays, CheckCircle2, Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';


import { useSubmitInquiry } from '../api';

import { PhoneField } from '@/components/phone-field';
import { RequestError } from '@/lib/api-client';
import { clientEnv } from '@/lib/env';
import { EASE_OUT_EXPO, PANEL_TRANSITION } from '@/lib/motion';


const TIME_SLOTS = ['10:00', '11:00', '12:00', '13:00', '15:00', '16:00', '17:00', '18:00'].map(
  (t) => ({ value: t, label: t }),
);

const localTimezone =
  typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;

/** Local date (YYYY-MM-DD) for the date input's `min` attribute. */
function todayIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

/**
 * Client-side form contract for the integrated inquiry. Validates the required
 * contact fields plus the visit slot when the "Schedule a site visit" toggle is
 * on. On submit it maps to the shared `CreateInquiryInput` — the backend
 * submission contract is unchanged.
 */
const InquiryFormSchema = z
  .object({
    name: z.string().trim().min(2, 'Please enter your full name').max(120),
    email: z.string().trim().email('Enter a valid email address'),
    phone: PhoneSchema,
    scheduleVisit: z.boolean().default(false),
    visitDate: z.string().optional(),
    visitTime: z.string().optional(),
    /** Honeypot — must stay empty. Named `website`, not `company`: an
     *  off-screen field called `company` is an autofill target for browser
     *  profiles and password managers, which would silently reject a real
     *  person's enquiry. */
    website: z.string().max(0).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.scheduleVisit) {
      if (!v.visitDate)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['visitDate'], message: 'Select a visit date' });
      if (!v.visitTime)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['visitTime'], message: 'Select a visit time' });
    }
  });
type InquiryFormValues = z.infer<typeof InquiryFormSchema>;

export function ContactCard({
  slug,
  title,
  bare = false,
}: {
  slug: string;
  title: string;
  /** Render without the card wrapper/heading — for embedding in a Modal, which
   *  already supplies its own surface + title. */
  bare?: boolean;
}) {
  const reduce = useReducedMotion();
  const mutation = useSubmitInquiry();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InquiryFormValues>({
    resolver: zodResolver(InquiryFormSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      scheduleVisit: false,
      visitDate: '',
      visitTime: '',
      website: '',
    },
  });

  const scheduleVisit = watch('scheduleVisit');

  const onSubmit = handleSubmit(async (values) => {
    const url = `${clientEnv.siteUrl}/property/${slug}`;
    // Auto-attach the current property's context (name + URL; slug resolves the
    // ID and name server-side) alongside the user's details.
    const payload: CreateInquiryInput = {
      type: values.scheduleVisit ? 'visit' : 'enquiry',
      name: values.name,
      email: values.email,
      phone: values.phone,
      propertySlug: slug,
      message: `Enquiry about ${title} — ${url}`,
      website: '',
      ...(values.scheduleVisit
        ? {
            visitSlot: {
              date: values.visitDate ?? '',
              time: values.visitTime ?? '',
              mode: 'in-person' as const,
              timezone: localTimezone,
            },
          }
        : {}),
    };
    try {
      await mutation.mutateAsync(payload);
      reset();
    } catch {
      // Surfaced below via mutation.isError; values are preserved for retry.
    }
  });

  const content = (
    <>
      {mutation.isSuccess ? (
        <InquirySuccess reduce={reduce ?? false} onReset={() => mutation.reset()} />
      ) : (
        <>
          {!bare && (
            <h2 className="text-lg font-semibold tracking-tight">Enquire about this property</h2>
          )}

          {/* Named for the listing so a screen reader can tell one enquiry form
              from another — the same form is also rendered inside a modal. */}
          <form
            onSubmit={onSubmit}
            className={cn('flex flex-col gap-4', !bare && 'mt-4')}
            noValidate
            aria-label={`Enquire about ${title}`}
          >
            {/* Honeypot — off-screen, hidden from users and assistive tech. */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              {...register('website')}
            />

            <Input
              label="Full name"
              autoComplete="name"
              placeholder="Your full name"
              required
              error={errors.name?.message}
              {...register('name')}
            />
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              required
              error={errors.email?.message}
              {...register('email')}
            />
            <PhoneField
              control={control}
              name="phone"
              label="Mobile number"
              required
              error={errors.phone?.message}
            />

            {/* Schedule a site visit toggle */}
            <label
              htmlFor="schedule-visit"
              className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-border bg-background px-3.5 py-3"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <CalendarDays className="size-4 text-primary" aria-hidden="true" />
                Schedule a site visit
              </span>
              <span className="relative inline-flex">
                <input
                  id="schedule-visit"
                  type="checkbox"
                  role="switch"
                  className="peer size-0 opacity-0"
                  {...register('scheduleVisit')}
                />
                <span
                  aria-hidden="true"
                  className="h-6 w-11 rounded-full bg-muted-foreground/30 transition-colors duration-fast ease-out-expo peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background"
                />
                {/* A touch of spring overshoot on the thumb's slide reads as a
                 *  tactile physical flick rather than a flat linear slide. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-base ease-spring peer-checked:translate-x-5"
                />
              </span>
            </label>

            <AnimatePresence initial={false}>
              {scheduleVisit && (
                <motion.div
                  key="visit-fields"
                  initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                  animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                  exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                  transition={PANEL_TRANSITION}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <Input
                      label="Visit date"
                      type="date"
                      min={todayIso()}
                      required
                      error={errors.visitDate?.message}
                      {...register('visitDate')}
                    />
                    <Select
                      label="Visit time"
                      placeholder="Select"
                      required
                      options={TIME_SLOTS}
                      error={errors.visitTime?.message}
                      defaultValue=""
                      {...register('visitTime')}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {mutation.isError && (
              <p
                className="rounded border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
                role="alert"
              >
                {mutation.error instanceof RequestError
                  ? mutation.error.message
                  : 'Something went wrong sending your enquiry. Please try again.'}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              loading={isSubmitting || mutation.isPending}
              className="w-full"
            >
              {!(isSubmitting || mutation.isPending) && (
                <Send className="size-4" aria-hidden="true" />
              )}
              Submit Inquiry
            </Button>
          </form>
        </>
      )}
    </>
  );

  if (bare) return content;
  return <div className="rounded-lg border border-border bg-card p-6 shadow-sm">{content}</div>;
}

function InquirySuccess({ reduce, onReset }: { reduce: boolean; onReset: () => void }) {
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE_OUT_EXPO }}
      className={cn('flex flex-col items-center gap-3 py-4 text-center')}
      role="status"
      aria-live="polite"
    >
      <motion.span
        initial={reduce ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.08, type: 'spring', stiffness: 260, damping: 18 }}
        className="flex size-14 items-center justify-center rounded bg-success/15 text-success"
      >
        <CheckCircle2 className="size-8" aria-hidden="true" strokeWidth={2} />
      </motion.span>
      <p className="font-medium">Enquiry sent — thank you!</p>
      <p className="text-sm text-muted-foreground">
        An advisor will contact you shortly to help with the next steps.
      </p>
      <Button variant="outline" size="sm" onClick={onReset}>
        Send another enquiry
      </Button>
    </motion.div>
  );
}
